import { useState, useMemo } from "react";
import { STORIES } from "../../data/ybf/stories";
import { VOICE_VS_PROJECT, SIGNAL_META } from "../../data/ybf/storyComparisons";
import { PILLARS } from "../../data/ybf/pillars";
import StoryCard from "../../components/ybf/StoryCard";
import StoryComparisonCard from "../../components/ybf/StoryComparisonCard";
import DeepDiveAnalysis from "../../components/ybf/DeepDiveAnalysis";
import { BookOpen, Mic, Activity, GitCompareArrows } from "lucide-react";

export default function Stories() {
  const [pillarFilter, setPillarFilter] = useState("all");
  const [tab, setTab] = useState("dual"); // "dual" | "stories"

  const filtered = useMemo(
    () =>
      STORIES.filter((s) => {
        if (pillarFilter !== "all" && s.pillar !== pillarFilter) return false;
        return true;
      }),
    [pillarFilter]
  );

  const voiceAiCount = STORIES.length;

  return (
    <div data-testid="ybf-stories" className="space-y-5 pb-8">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-[#E2231A]" />
          <h1 className="text-2xl font-bold text-slate-900">Stories from the Field</h1>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Collected via <span className="font-semibold text-violet-700">Voice AI agents</span> across Lenovo CSR programmes.
        </p>
      </div>

      {/* Source summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SourceTile icon={Mic}       label="Voice AI"      count={voiceAiCount}   color="violet" />
        <SourceTile icon={BookOpen}  label="Total stories" count={STORIES.length} color="slate" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 w-fit" data-testid="stories-tabs">
        <button
          type="button"
          onClick={() => setTab("dual")}
          data-testid="stories-tab-dual"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
            tab === "dual"
              ? "bg-[#1A1A1A] text-white shadow"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Activity size={12} /> Voice AI Pulse vs Project Health
        </button>
        <button
          type="button"
          onClick={() => setTab("deepdive")}
          data-testid="stories-tab-deepdive"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
            tab === "deepdive"
              ? "bg-[#1A1A1A] text-white shadow"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <GitCompareArrows size={12} /> Deep Dive Analysis
        </button>
        <button
          type="button"
          onClick={() => setTab("stories")}
          data-testid="stories-tab-stories"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
            tab === "stories"
              ? "bg-[#1A1A1A] text-white shadow"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <BookOpen size={12} /> Stories from the Field
        </button>
      </div>

      {/* Voice AI Pulse vs Project Health section */}
      {tab === "dual" && (
      <div data-testid="dual-source-section" className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
          <div>
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-[#1A1A1A]" />
              <h2 className="text-base font-bold text-slate-900">
                Voice AI Pulse vs Project Health
              </h2>
            </div>
            <p className="text-[12px] text-slate-600 mt-1 max-w-3xl">
              A single beneficiary's <span className="font-semibold text-violet-700">Voice AI sample</span> placed beside the
              {" "}<span className="font-semibold text-teal-700">aggregate project health</span>. Where they agree, the data
              validates. Where they diverge, the ground tells us something the dashboard alone can't.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold">
            {Object.entries(SIGNAL_META).filter(([k]) => k !== "neutral").map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 text-slate-600">
                <span className="w-2 h-2 rounded-full" style={{ background: v.fill }} />
                {v.label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {VOICE_VS_PROJECT.map((c) => (
            <StoryComparisonCard key={c.id} comparison={c} />
          ))}
        </div>
      </div>
      )}

      {/* Deep Dive Analysis section */}
      {tab === "deepdive" && <DeepDiveAnalysis />}

      {/* Stories tab content */}
      {tab === "stories" && (
      <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pillar:</span>
          <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200">
            <FilterBtn active={pillarFilter === "all"} onClick={() => setPillarFilter("all")} label="All" />
            {PILLARS.map((p) => (
              <FilterBtn
                key={p.id}
                active={pillarFilter === p.id}
                onClick={() => setPillarFilter(p.id)}
                label={p.short}
                accent={p.color}
              />
            ))}
          </div>
        </div>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} of {STORIES.length} stories</span>
      </div>

      {/* Story grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((s) => <StoryCard key={s.id} story={s} />)}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
          No stories match the selected filters.
        </div>
      )}
      </>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, label, accent }) {
  return (
    <button
      onClick={onClick}
      data-testid={`stories-filter-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${
        active ? "text-white shadow" : "text-slate-600 hover:bg-slate-50"
      }`}
      style={active ? { background: accent || "#1A1A1A" } : {}}
    >
      {label}
    </button>
  );
}

function SourceTile({ icon: Icon, label, count, color }) {
  const map = {
    violet: { bg: "bg-violet-50",  border: "border-violet-200", text: "text-violet-700" },
    teal:   { bg: "bg-teal-50",    border: "border-teal-200",   text: "text-teal-700"   },
    slate:  { bg: "bg-slate-50",   border: "border-slate-200",  text: "text-slate-700"  },
  }[color];
  return (
    <div className={`${map.bg} ${map.border} border rounded-2xl p-4 flex items-center gap-3`}
         data-testid={`source-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={26} className={map.text} />
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{count}</div>
        <div className="text-xs text-slate-600 mt-1">{label}</div>
      </div>
    </div>
  );
}


